/**
 * The loading window's rules as data (the loading standard, design screens 9 and 10, 26 October 2026): what each read
 * a view waits on looks like as a line, and when the window shows, says it is slow, and closes. Pure, so the window
 * and the hook that feeds it agree.
 */

/** The window waits this long before it shows, so a view that is quick never flashes it. */
export const SHOW_AFTER_MS = 400
/** It closes this long after the last read is in, so the last tick is seen. */
export const CLOSE_AFTER_MS = 300
/** A read still going after this long says so in words. */
export const SLOW_AFTER_MS = 8_000
/** After this long the window offers Try again beside Keep working. */
export const STUCK_AFTER_MS = 30_000
/** The window is for several reads; one read has its skeleton alone. */
export const MIN_LINES = 2

export type LoadingLineState = 'loading' | 'done' | 'failed' | 'answered'

/** One read as the window lists it. */
export interface LoadingLine {
  key: string
  /** What is being read, in the reader's words, never an endpoint ("The board, brought up to now"). */
  label: string
  state: LoadingLineState
  /** For `failed`, what went wrong; for `answered`, the answer in place of data ("Not released yet"). */
  note: string | null
  /** Said under the line once it has taken `SLOW_AFTER_MS`. */
  slowNote: string | null
}

/** The parts of a TanStack query the window reads, so the hook takes any query. */
export interface QueryLike {
  isPending: boolean
  isError: boolean
  fetchStatus: 'fetching' | 'paused' | 'idle'
  error: unknown
  refetch: () => unknown
}

export interface LoadingSource {
  key: string
  label: string
  query: QueryLike
  slowNote?: string
  /** An error that is an answer rather than a failure (a month not released yet): the words its line shows, else null. */
  answered?: (error: unknown) => string | null
  /** Left out this time (a read the view doesn't need now). */
  skip?: boolean
}

function errorText(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'It could not be loaded.'
}

/**
 * A source as a line, or null when it is not part of the wait: skipped, or a query that is switched off (pending but
 * not fetching - TanStack's state for `enabled: false`).
 */
export function lineOf(source: LoadingSource): LoadingLine | null {
  const { query } = source
  if (source.skip || (query.isPending && query.fetchStatus === 'idle')) {
    return null
  }
  const base = { key: source.key, label: source.label, slowNote: source.slowNote ?? null }
  // A failed read being tried again is loading again.
  if (query.isPending || (query.isError && query.fetchStatus !== 'idle')) {
    return { ...base, state: 'loading', note: null }
  }
  if (query.isError) {
    const answer = source.answered?.(query.error) ?? null
    return answer !== null ? { ...base, state: 'answered', note: answer } : { ...base, state: 'failed', note: errorText(query.error) }
  }
  return { ...base, state: 'done', note: null }
}

export type WindowPhase = 'waiting' | 'open' | 'closed'

/** Where the window stands after a moment has passed: it shows once `SHOW_AFTER_MS` is up with several reads still going, and closes once they are in. */
export function nextPhase(phase: WindowPhase, lines: readonly LoadingLine[], sinceStart: number): WindowPhase {
  const loading = lines.some((line) => line.state === 'loading')
  if (phase === 'waiting') {
    if (!loading) {
      return 'closed'
    }
    return sinceStart >= SHOW_AFTER_MS && lines.length >= MIN_LINES ? 'open' : 'waiting'
  }
  return phase
}

/** Whether an open window has nothing left to wait for or show: every read in, none failed. */
export function isFinished(lines: readonly LoadingLine[]): boolean {
  return lines.every((line) => line.state === 'done' || line.state === 'answered')
}

/** "0.8 s", "6 s", "1 min 4 s". */
export function formatSeconds(ms: number): string {
  if (ms < 10_000) {
    return `${(Math.max(0, ms) / 1000).toFixed(1).replace(/\.0$/, '')} s`
  }
  const seconds = Math.round(ms / 1000)
  return seconds < 60 ? `${seconds} s` : `${Math.floor(seconds / 60)} min ${seconds % 60} s`
}

/** One showing of the window: the views it is for, when it started, and when each read started and ended. */
export interface LoadingSession {
  /** The views shown when it started (and any that joined since), by owner, with the visit each was on. */
  visits: Readonly<Record<string, string>>
  startedAt: number
  phase: WindowPhase
  started: Readonly<Record<string, number>>
  ended: Readonly<Record<string, number>>
  /** When every read was last seen in, for the close's short pause. */
  finishedAt: number | null
}

/**
 * The session after a change or a moment: a new one when a view it was for is no longer shown (another tab, another
 * facility), the same one when views only joined it - so the dashboard's reads and then the tab's own share one window,
 * and a tab revisited with its data already read starts closed and stays so.
 */
export function advanceSession(session: LoadingSession | null, visits: Readonly<Record<string, string>>, lines: readonly LoadingLine[], now: number): LoadingSession | null {
  if (Object.keys(visits).length === 0) {
    return null
  }
  const continuing = session !== null && Object.entries(session.visits).every(([owner, visit]) => visits[owner] === visit)
  const loading = lines.some((line) => line.state === 'loading')
  const base: LoadingSession = continuing
    ? { ...session, visits }
    : { visits, startedAt: now, phase: loading ? 'waiting' : 'closed', started: {}, ended: {}, finishedAt: null }
  const started = { ...base.started }
  const ended = { ...base.ended }
  for (const line of lines) {
    if (line.state === 'loading') {
      if (ended[line.key] !== undefined) {
        // Tried again: timed afresh.
        delete ended[line.key]
        started[line.key] = now
      } else if (started[line.key] === undefined) {
        started[line.key] = continuing ? now : base.startedAt
      }
    } else if (ended[line.key] === undefined) {
      started[line.key] ??= base.startedAt
      ended[line.key] = now
    }
  }
  let phase = nextPhase(base.phase, lines, now - base.startedAt)
  let finishedAt = base.finishedAt
  if (phase === 'open') {
    finishedAt = isFinished(lines) ? (finishedAt ?? now) : null
    if (finishedAt !== null && now - finishedAt >= CLOSE_AFTER_MS) {
      phase = 'closed'
    }
  }
  return { ...base, started, ended, phase, finishedAt }
}
